// features/workstations/dashboard-sections/registry.js
// ═══════════════════════════════════════════════════════════
// SECTION REGISTRY — maps a section_key (from
// workstation.config.tab_config.dashboard.sections) to a
// React component.
//
// Sections that aren't yet built render as a styled
// SectionPlaceholder so the dashboard always renders cleanly
// without breaking when the config references something we
// haven't shipped yet.
//
// Adding a new section:
//   1. Drop a file in this directory exporting a default
//      component that takes { workstation, config } props.
//   2. Add an entry below to SECTION_COMPONENTS.
//   3. Add the section_key to a workstation's
//      config.tab_config.dashboard.sections array (or update
//      the seed SQL).
// ═══════════════════════════════════════════════════════════

import SectionPlaceholder from './section-placeholder';

// ─── Day Trading sections (originally registered) ─────────
import ThesisSection from './thesis';
import StudiesSection from './studies';
import ProcessChecklistSection from './process-checklist';
import WindowRiskMapSection from './window-risk-map';
import PatternLibraryStatusSection from './pattern-library-status';

// ─── Long-Term 10x sections (originally registered) ───────
import MacroGateSection from './macro-gate';
import PipelineIntelligenceSection from './pipeline-intelligence';
import PreExplosionMatrixSection from './pre-explosion-matrix';

// ─── Commit 21: Wire up the rest of Tim's widget components ──
// All 23 of these files exist on disk as full implementations
// (449-661 lines each) but were never registered in the
// SECTION_COMPONENTS map. Without registration, the widget grid
// falls back to SectionPlaceholder ("This section ships with
// live wiring in Phase 3b…") even though the rich code is
// right there. This is the wiring fix.
import DailyReadSection from './daily-read';
import JarvisBriefingSection from './jarvis-briefing';
import ThesesSection from './theses';
import ForceTrackerSection from './force-tracker';
import SentimentSection from './sentiment';
import KillSwitchSection from './kill-switch';
import StrategicDialogueSection from './strategic-dialogue';
import ThesisStressTestSection from './thesis-stress-test';
import OutcomeCalendarSection from './outcome-calendar';
import GexLevelsSection from './gex-levels';
import PersonalPlaybookSection from './personal-playbook';
import MorningRoutineSection from './morning-routine';
import DecisionJournalSection from './decision-journal';
import MistakeLogSection from './mistake-log';
import StrategyHealthSection from './strategy-health';
import PositionSizingSection from './position-sizing';
import NewsCorrelationSection from './news-correlation';
import DteConditionsSection from './dte-conditions';
import PreMarketSection from './pre-market';
import MacroConditionsSection from './macro-conditions';
import WeeklyReviewSection from './weekly-review';
import FlaggedCompaniesSection from './flagged-companies';
import EntityComparisonSection from './entity-comparison';

const SECTION_COMPONENTS = {
  // Day Trading
  thesis: ThesisSection,
  studies: StudiesSection,
  process_checklist: ProcessChecklistSection,
  window_risk_map: WindowRiskMapSection,
  pattern_library_status: PatternLibraryStatusSection,

  // Long-Term 10x
  macro_gate: MacroGateSection,
  pipeline_intelligence: PipelineIntelligenceSection,
  pre_explosion_matrix: PreExplosionMatrixSection,

  // ─── Commit 21: full widget grid restoration ───
  // Keys must match lib/widgets/registry.js — single source of
  // truth for what users see in the customize panel.
  daily_read:                 DailyReadSection,
  jarvis_briefing:            JarvisBriefingSection,
  theses:                     ThesesSection,
  force_tracker:              ForceTrackerSection,
  sentiment:                  SentimentSection,
  kill_switch:                KillSwitchSection,
  strategic_dialogue:         StrategicDialogueSection,
  // thesis_health is the legacy key for thesis_stress_test —
  // both keys map to the same component so old workstation
  // configs keep working.
  thesis_stress_test:         ThesisStressTestSection,
  thesis_health:              ThesisStressTestSection,
  outcome_calendar_heatmap:   OutcomeCalendarSection,
  gex_levels:                 GexLevelsSection,
  personal_playbook:          PersonalPlaybookSection,
  morning_routine:            MorningRoutineSection,
  decision_journal:           DecisionJournalSection,
  mistake_log:                MistakeLogSection,
  strategy_health:            StrategyHealthSection,
  position_sizing:            PositionSizingSection,
  news_correlation:           NewsCorrelationSection,
  dte_conditions:             DteConditionsSection,
  pre_market:                 PreMarketSection,
  macro_conditions:           MacroConditionsSection,
  weekly_review:              WeeklyReviewSection,
  // new_companies_flagged + entity_deep_dive_workspace are the
  // long-term workstation companion widgets to flagged-companies
  // + entity-comparison files. Map both legacy key shapes.
  new_companies_flagged:      FlaggedCompaniesSection,
  flagged_companies:          FlaggedCompaniesSection,
  entity_deep_dive_workspace: EntityComparisonSection,
  entity_comparison:          EntityComparisonSection,
};

/**
 * Look up the component for a section key.
 * Returns the component or a placeholder factory.
 * Caller renders: <Component workstation={ws} sectionKey={key} />
 */
export function getSectionComponent(sectionKey) {
  return SECTION_COMPONENTS[sectionKey] || SectionPlaceholder;
}

/**
 * Friendly title for a section key — used by the placeholder
 * and as the default header label for any section that
 * doesn't override it internally.
 */
const SECTION_TITLES = {
  thesis: 'Today\u2019s Thesis',
  pre_market: 'Pre-Market Read',
  studies: 'Studies Confluence',
  sentiment: 'Sentiment',
  window_risk_map: 'Window Risk Map',
  news_correlation: 'News Correlation',
  dte_conditions: '0DTE Conditions',
  pattern_library_status: 'Pattern Library Status',
  process_checklist: 'Process Checklist',

  daily_read: 'Today\u2019s Read',
  macro_gate: 'Macro Gate',
  pipeline_intelligence: 'Pipeline Intelligence',
  pre_explosion_matrix: 'Pre-Explosion Matrix',
  position_sizing: 'Position Sizing',
  thesis_health: 'Thesis Health',
  new_companies_flagged: 'New Companies Flagged',
  macro_conditions: 'Macro Conditions',
};

export function sectionTitle(key) {
  return SECTION_TITLES[key] || titleize(key);
}

function titleize(s) {
  return String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
